import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { Badge } from "@/components/badge";
import { Fieldset, Legend } from "@/components/fieldset";
import { ArrowLeftIcon, PencilIcon } from "@heroicons/react/20/solid";
import { useQueryGetExamConfig } from "@/apis/exam_configs/getExamConfig";
import { useQueryClient } from "@tanstack/react-query";
import SubjectDomainManager from "./components/SubjectDomainManager";

const ExamConfigDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  if (!id) {
    navigate("/exam_configs");
    return null;
  }

  // Query exam config data
  const { data: examConfig, isLoading, error } = useQueryGetExamConfig(id);

  // Go back to list
  const handleBack = () => {
    window.history.back();
  };

  // Go to edit page
  const handleEdit = () => {
    navigate(`/exam_configs/${id}/edit`);
  };

  // Calculate total duration and calculator subjects
  const getTotalDuration = () => {
    if (!examConfig?.subjects) return 0;
    return examConfig.subjects.reduce(
      (total, subject) => total + subject.duration_default,
      0
    );
  };

  const getTotalQuestions = () => {
    if (!examConfig?.subjects) return 0;
    return examConfig.subjects.reduce(
      (total, subject) => total + subject.question_count_default,
      0
    );
  };

  const getCalculatorSubjects = () => {
    if (!examConfig?.subjects) return 0;
    return examConfig.subjects.filter((subject) => subject.calculator_allowed)
      .length;
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading exam configuration...</Text>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (error || !examConfig) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam configuration. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button plain onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>

            <div>
              <Heading>Exam Configuration: {examConfig.id}</Heading>
              <Text className="text-zinc-500">
                View complete information about this exam configuration.
              </Text>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleEdit}>
              <PencilIcon className="h-4 w-4" />
              Edit
            </Button>
          </div>
        </div>

        {/* Subjects Details */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <Fieldset>
            <Legend>Subjects</Legend>
            <div className="mt-4 space-y-6">
              {examConfig.subjects.map((subject, index) => (
                <div
                  key={index}
                  className="p-6 border border-gray-200 rounded-md bg-gray-50 space-y-4"
                >
                  {/* Subject Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h4 className="text-lg font-medium text-gray-900">
                        {subject.subject}
                      </h4>
                      {subject.calculator_allowed && (
                        <Badge color="green">Calculator Allowed</Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 space-y-1">
                      <div>
                        <span className="font-medium">
                          {subject.duration_default}
                        </span>{" "}
                        minutes
                      </div>
                      <div>
                        <span className="font-medium">
                          {subject.question_count_default}
                        </span>{" "}
                        questions
                      </div>
                      <div>
                        <span className="font-medium">
                          {subject.choice_count ?? 4}지선다
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Domain & Category Management */}
                  <div className="border-t border-gray-200 pt-4">
                    <SubjectDomainManager
                      examType={examConfig.id}
                      subject={subject.subject}
                      domains={subject.domains || []}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Fieldset>
        </div>
      </div>
    </PageContainer>
  );
};

export default ExamConfigDetailPage;
